import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import type { QueueItem } from "@/interfaces/adjustments";

/**
 * The commit step for a decision.
 *
 * Approving mints credits and posts a journal entry in one transaction, immediately. Everywhere
 * else money moves in this app there is a review before it; a single click adjacent to a text field
 * someone may be tabbing through is not that. The prompt restates the amount and the target rather
 * than asking "are you sure", so the thing being confirmed is the thing being read.
 *
 * The note lives here rather than on the card: on a queue of a hundred, a note box per row is a
 * hundred fields nobody typed in.
 */
export default function ConfirmDecisionDialog({
  item,
  approve,
  onClose,
  onConfirm,
  busy,
}: {
  item: QueueItem | null;
  approve: boolean;
  onClose: () => void;
  onConfirm: (note: string | undefined) => Promise<void>;
  busy: boolean;
}) {
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  if (!item) return null;

  const removing = item.summary?.variant === "negative";
  const prompt = approve
    ? (item.summary?.confirmPrompt ?? item.summary?.headline ?? "Approve this request?")
    : `Reject this request? ${item.summary?.headline ?? ""} Nothing will move.`;

  return (
    <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontFamily: "var(--font-lato)", fontWeight: 700 }}>
        {approve ? "Approve this request" : "Reject this request"}
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
        <Typography sx={{ fontSize: 14 }}>{prompt}</Typography>

        {approve && removing ? (
          <Typography sx={{ fontSize: 13, color: "var(--color-error, #b3261e)" }}>
            Credits are drawn from the group's lots as soon as you approve. There is no undo.
          </Typography>
        ) : null}

        {(item.summary?.facts ?? []).length > 0 ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {item.summary?.facts?.map((f) => (
              <Box key={f.label} sx={{ display: "flex", gap: 1, fontSize: 13 }}>
                <Typography sx={{ fontSize: 13, color: "var(--color-graphite)", minWidth: 120 }}>
                  {f.label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: f.tone === "warn" ? 700 : 500,
                    color: f.tone === "warn" ? "var(--color-error, #b3261e)" : undefined,
                  }}
                >
                  {f.value}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : null}

        <TextField
          size="small"
          multiline
          minRows={2}
          label={approve ? "Note (optional)" : "Reason for rejecting"}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />

        {error ? (
          <Typography sx={{ fontSize: 13, color: "var(--color-error, #b3261e)" }}>
            {error}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={approve ? "primary" : "error"}
          disabled={busy}
          onClick={async () => {
            setError(null);
            try {
              await onConfirm(note.trim() || undefined);
            } catch (e) {
              setError(e instanceof Error ? e.message : "That did not work");
            }
          }}
        >
          {busy ? "Working…" : approve ? "Approve" : "Reject"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
